import {
  BellIcon,
  CogIcon,
  GlobeAltIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { SelectItem } from "@heroui/react";
import AppSelect from "../../components/common/appSelect";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "../../utils/toast";
import aboutIcon from "../../assets/settingPage/aboutIcon.svg";
import bellIcon from "../../assets/settingPage/bellIcon.svg";
import languageIcon from "../../assets/settingPage/languageIcon.svg";
import linkIcon from "../../assets/settingPage/linkIcon.svg";
import privacyIcon from "../../assets/settingPage/privacyIcon.svg";
import profileIcon from "../../assets/settingPage/profileIcon.svg";
import termsIcon from "../../assets/settingPage/termsAndConditionIcon.svg";
import { GhostButton, PrimaryButton } from "../../components/profile/edit/formPrimitives";
import { UpdateProfileFeature } from "../../features/profile/updateProfileFeature";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import {
  changeLanguage,
  fetchCountries,
  getAllSubregions,
  getProfileForSetting,
  getProfileUser,
  updateNotificationSetting,
} from "../../services/api";
import "../../styles/settings.css";

import {
  EventPreferenceDto,
  NotificationSettingRequestDto,
  Profile,
} from "../../types/types";
import { ProfileTopBarActions } from "../../types/updateProfileProps";
import StripeLinkCard from "./stripeLinkCard";

interface SettingsData {
  language: string;
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
    marketing: boolean;
    regions: string[];
  };
  theme: "light" | "dark" | "system";
  privacy: {
    profileVisible: boolean;
    showEmail: boolean;
    showActivity: boolean;
    dataCollection: boolean;
  };
}

interface Region {
  code: string;
  name: string;
}

const ALL_COUNTRIES_KEY = "All_Countries";

interface SidebarItem {
  key: string;
  label: string;
  iconSrc?: string;
  icon?: React.ReactNode;
}

const SettingsAssetIcon = ({
  src,
  active,
  className = "size-7",
}: {
  src: string;
  active: boolean;
  className?: string;
}) => (
  <span
    aria-hidden="true"
    className={`block ${className} ${active ? "bg-blue-600" : "bg-neutral-600"}`}
    style={{
      WebkitMask: `url(${src}) center / contain no-repeat`,
      mask: `url(${src}) center / contain no-repeat`,
    }}
  />
);

const Settings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isDesktop = useIsDesktop();
  const tabParam = searchParams.get("tab");
  const [activeSection, setActiveSection] = useState<string>(tabParam || "profile");
  const [mobileSubPageOpen, setMobileSubPageOpen] = useState<boolean>(Boolean(tabParam));
  // Cancel / Save changes of the Profile tab (provided by UpdateProfileFeature)
  const [profileActions, setProfileActions] = useState<ProfileTopBarActions | null>(null);

  const contentScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentScrollRef.current) contentScrollRef.current.scrollTop = 0;
    window.scrollTo(0, 0);
    const main = document.querySelector('main[data-app-scroll-container="true"]');
    if (main) main.scrollTop = 0;
  }, [activeSection]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [regionOptions, setRegionOptions] = useState<Region[]>([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [countries, setCountries] = useState<string[]>([]);
  const { t, i18n } = useTranslation("settings");
  const savedProfile = localStorage.getItem("loginResponse");
  const parsedProfile = savedProfile ? JSON.parse(savedProfile) : null;
  const loggedInUserId = parsedProfile?.id;
  const loggedInUserName = parsedProfile?.username;

  useEffect(() => {
    if (tabParam) {
      setActiveSection(tabParam);
      setMobileSubPageOpen(true);
    } else {
      setMobileSubPageOpen(false);
    }
  }, [tabParam]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem("i18nextLng") || "en";
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  useEffect(() => {
    if (tabParam && tabParam !== activeSection) {
      setActiveSection(tabParam);
    }
  }, [tabParam]);

  const defaultEventPreferences: EventPreferenceDto[] = [
    { eventName: "CARD_PUBLISH", emailNotify: false, pushNotify: false },
    { eventName: "DEAL_PROPOSE", emailNotify: false, pushNotify: false },
    { eventName: "DEAL_CONFIRM", emailNotify: false, pushNotify: false },
    { eventName: "CONTACT_INVITE", emailNotify: false, pushNotify: false },
    { eventName: "CONTACT_ACCEPT", emailNotify: false, pushNotify: false },
  ];

  const [eventPreferences, setEventPreferences] = useState<EventPreferenceDto[]>(defaultEventPreferences);
  const [initialNotificationSettings, setInitialNotificationSettings] = useState<{
    countries: string[];
    regions: string[];
    events: EventPreferenceDto[];
  } | null>(null);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [fullProfile, setFullProfile] = useState<Profile | null>(null);
  const [profileSettingId, setProfileSettingId] = useState<number | null>(null);

  const [errors, setErrors] = useState({
    country: "",
  });

  const [settings, setSettings] = useState<SettingsData>({
    language: i18n.language || "en",
    notifications: {
      email: true,
      push: true,
      sms: false,
      marketing: false,
      regions: [],
    },
    theme: "system",
    privacy: {
      profileVisible: true,
      showEmail: false,
      showActivity: true,
      dataCollection: true,
    },
  });

  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    i18n.language?.replace(/"/g, "") || "en"
  );
  const [savingLanguage, setSavingLanguage] = useState(false);

  const languageNames: { [key: string]: string } = {
    en: t("language_names.en", { defaultValue: "English" }),
    de: t("language_names.de", { defaultValue: "Deutsch" }),
    hr: t("language_names.hr", { defaultValue: "Hrvatski" }),
  };

  const sidebarItems: SidebarItem[] = [
    {
      key: "profile",
      label: t("profile", { defaultValue: "Profile" }),
      iconSrc: profileIcon,
    },
    {
      key: "language",
      label: t("language_preference", { defaultValue: "Language preference" }),
      iconSrc: languageIcon,
    },
    {
      key: "notifications",
      label: t("notification_preference", { defaultValue: "Notification Preference" }),
      iconSrc: bellIcon,
    },
    {
      key: "privacy",
      label: t("privacy_settings", { defaultValue: "Privacy settings" }),
      iconSrc: privacyIcon,
    },
    {
      key: "linkAccount",
      label: t("link_account", { defaultValue: "Link account" }),
      iconSrc: linkIcon,
    },
    {
      key: "about",
      label: t("about", { defaultValue: "About" }),
      iconSrc: aboutIcon,
    },
    {
      key: "terms",
      label: t("terms_conditions", { defaultValue: "Terms & conditions" }),
      iconSrc: termsIcon,
    },
  ];

  const validateForm = () => {
    const newErrors = {
      country: selectedCountries.length > 0 ? "" : t("at_least_one_country"),
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((e) => e === "");
  };

  const formatEventName = (eventName: string): string => {
    return eventName
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const countryData = await fetchCountries();
        const withAll = [ALL_COUNTRIES_KEY, ...countryData];
        setCountries(withAll);

        setLoadingRegions(true);
        const allSubregions = await getAllSubregions();
        const formattedRegions: Region[] = allSubregions.map((subregion) => ({
          code: subregion,
          name: subregion,
        }));
        setRegionOptions(formattedRegions);
      } catch (error) {
        toast.error(t("failed_load_data"));
      } finally {
        setLoadingRegions(false);
      }
    };

    loadInitialData();

    if (!loggedInUserName) return;

    getProfileForSetting(loggedInUserName)
      .then((data) => {
        setProfile(data);

        const setting = data.profileSetting?.notificationSetting;
        if (setting) {
          setProfileSettingId(data.profileSetting?.id ?? null);
          const initialCountries = setting.countries || [];
          const initialRegions = setting.regions || [];
          setSelectedCountries(initialCountries);
          setSelectedRegions(initialRegions);

          const updatedPreferences = defaultEventPreferences.map((pref) => {
            const match = setting.events.find((e) => e.name === pref.eventName);
            return {
              ...pref,
              emailNotify: match?.emailNotify ?? false,
              pushNotify: match?.pushNotify ?? false,
            };
          });
          setEventPreferences(updatedPreferences);
          setInitialNotificationSettings({
            countries: initialCountries,
            regions: initialRegions,
            events: updatedPreferences,
          });
        }
      })
      .catch(() => {
        toast.error(t("failed_load_profile"));
      });

    if (loggedInUserId) {
      getProfileUser(loggedInUserId, loggedInUserName)
        .then((fullData) => {
          setFullProfile(fullData);
        })
        .catch((err) => {
          console.error("Failed to load full profile:", err);
        });
    }
  }, [t, loggedInUserId, loggedInUserName]);

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error(t("validation_failed"));
      return;
    }

    if (!profileSettingId) {
      toast.error(t("missing_profile_setting_id"));
      return;
    }

    if (!loggedInUserName) return;

    const payload: NotificationSettingRequestDto = {
      countries: selectedCountries,
      regions: selectedRegions,
      profileSettingId,
      eventPreferences,
    };

    try {
      setSavingNotifications(true);
      await updateNotificationSetting(loggedInUserName, payload);
      setInitialNotificationSettings({
        countries: [...selectedCountries],
        regions: [...selectedRegions],
        events: eventPreferences.map((p) => ({ ...p })),
      });
      toast.success(t("notifications_save_success"));
    } catch {
      toast.error(t("notifications_save_failed"));
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleCancelNotifications = () => {
    if (!initialNotificationSettings) return;
    setSelectedCountries([...initialNotificationSettings.countries]);
    setSelectedRegions([...initialNotificationSettings.regions]);
    setEventPreferences(initialNotificationSettings.events.map((p) => ({ ...p })));
    setErrors({ country: "" });
  };

  const hasNotificationChanges = React.useMemo(() => {
    if (!initialNotificationSettings) return false;
    const countriesChanged =
      JSON.stringify([...selectedCountries].sort()) !==
      JSON.stringify([...initialNotificationSettings.countries].sort());
    const regionsChanged =
      JSON.stringify([...selectedRegions].sort()) !==
      JSON.stringify([...initialNotificationSettings.regions].sort());
    const eventsChanged =
      JSON.stringify(eventPreferences) !==
      JSON.stringify(initialNotificationSettings.events);
    return countriesChanged || regionsChanged || eventsChanged;
  }, [selectedCountries, selectedRegions, eventPreferences, initialNotificationSettings]);

  const handleSaveLanguage = async () => {
    try {
      setSavingLanguage(true);
      await i18n.changeLanguage(selectedLanguage);
      await changeLanguage(selectedLanguage);
      setSettings((prev) => ({ ...prev, language: selectedLanguage }));
      localStorage.setItem("i18nextLng", selectedLanguage);
      localStorage.setItem(
        "appSettings",
        JSON.stringify({
          ...settings,
          language: selectedLanguage,
          savedAt: new Date().toISOString(),
        })
      );
      toast.success(t("language_update_success", { defaultValue: "Language updated successfully" }));
    } catch (error) {
      toast.error(t("language_update_failed", { defaultValue: "Failed to update language" }));
    } finally {
      setSavingLanguage(false);
    }
  };

  const handleCancelLanguage = () => {
    setSelectedLanguage(settings.language.replace(/"/g, "") || "en");
  };

  const hasLanguageChanges = selectedLanguage !== (settings.language.replace(/"/g, "") || "en");

  useEffect(() => {
    if (settings.language) {
      setSelectedLanguage(settings.language.replace(/"/g, ""));
    }
  }, [settings.language]);

  const handleLanguageChange = async (value: string) => {
    try {
      await i18n.changeLanguage(value);
      await changeLanguage(value);
      setSettings((prev) => ({ ...prev, language: value }));
      localStorage.setItem("i18nextLng", value);
      localStorage.setItem(
        "appSettings",
        JSON.stringify({
          ...settings,
          language: value,
          savedAt: new Date().toISOString(),
        })
      );
      toast.success(t("language_update_success"));
    } catch (error) {
      toast.error(t("language_update_failed"));
    }
  };

  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setSettings((prev) => ({ ...prev, language: lng }));
    };
    i18n.on("languageChanged", handleLanguageChanged);
    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, [i18n]);

  const renderLanguageSection = () => (
    <div className="flex flex-col gap-4 font-['Plus_Jakarta_Sans']">
      <div className="hidden lg:flex items-center gap-2 border-b border-gray-100 pb-3">
        <SettingsAssetIcon src={languageIcon} active className="size-5" />
        <h3 className="text-lg font-bold text-gray-900">{t("language")}</h3>
      </div>
      <div>
        <h4 className="text-base font-semibold text-gray-900">
          {t("language_preference", "Language Preference")}
        </h4>
        <p className="text-gray-600 text-sm mt-1">
          {t("language_description", "Select your preferred language for the application interface.")}
        </p>
      </div>
      <div className="flex flex-col gap-1.5 max-w-md">
        <label className="text-sm font-medium text-gray-700">
          {t("language", "Language")} <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            className="w-full h-11 px-4 pr-10 bg-[#EAEAEA] text-gray-900 rounded-2xl text-sm font-medium appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 border-0"
          >
            {Object.entries(languageNames).map(([key, name]) => (
              <option key={key} value={key}>
                ({key.toUpperCase()}){name}
              </option>
            ))}
          </select>
          <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>
          </div>
        </div>
      </div>
      <div className="hidden lg:block mt-4 p-3 bg-blue-50 rounded-xl max-w-md">
        <p className="text-sm text-blue-700">
          <strong>{t("current_language")}:</strong> {languageNames[settings.language]}
        </p>
        <p className="text-xs text-blue-600 mt-1">
          {t("language_code")}: {settings.language}
        </p>
      </div>
    </div>
  );

  const renderNotificationSection = () => (
    <div className="flex flex-col gap-6 font-['Plus_Jakarta_Sans']">
      <div className="border-b border-gray-100 pb-3">
        <h3 className="text-lg font-bold text-gray-900">{t("notifications")}</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-gray-700 block mb-1">
            {t("countries")}
          </label>
          <AppSelect
            placeholder={t("select_countries")}
            selectionMode="multiple"
            selectedKeys={new Set(selectedCountries)}
            onSelectionChange={(keys) => {
              const values = Array.from(keys).map(String);

              setSelectedCountries((previousCountries) => {
                const hasAllCountries = values.includes(ALL_COUNTRIES_KEY);
                const hadAllCountries = previousCountries.includes(ALL_COUNTRIES_KEY);

                if (!hasAllCountries) {
                  return values;
                }

                if (!hadAllCountries) {
                  return [ALL_COUNTRIES_KEY];
                }

                return values.filter((country) => country !== ALL_COUNTRIES_KEY);
              });

              setErrors((prev) => ({ ...prev, country: "" }));
            }}
            size="sm"
            startContent={<GlobeAltIcon className="w-4 h-4" />}
            renderValue={(items) =>
              items
                .map((item) =>
                  item.key === ALL_COUNTRIES_KEY ? "All Countries" : item.textValue
                )
                .join(", ")
            }
          >
            {countries.map((country) => (
              <SelectItem key={country} textValue={country}>
                {country === ALL_COUNTRIES_KEY ? "All Countries" : country}
              </SelectItem>
            ))}
          </AppSelect>

          {errors.country && (
            <p className="text-xs text-red-500 mt-1">{errors.country}</p>
          )}
        </div>
      </div>

      <div className="lg:hidden flex flex-col gap-4">
        {eventPreferences.map((pref, index) => (
          <div
            key={`${pref.eventName}-${index}`}
            className="bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm"
          >
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-xs text-gray-500 font-medium">Notification Type</p>
              <p className="text-base font-bold text-gray-900 mt-0.5">{formatEventName(pref.eventName)}</p>
            </div>
            <div className="px-4 py-3.5 border-b border-gray-100 flex items-center justify-between">
              <span className="text-sm text-gray-700 font-medium">Email Notification</span>
              <button
                type="button"
                onClick={() => {
                  setEventPreferences((prev) =>
                    prev.map((p) =>
                      p.eventName === pref.eventName
                        ? { ...p, emailNotify: !p.emailNotify }
                        : p
                    )
                  );
                }}
                className={`size-5 rounded-md flex items-center justify-center transition-colors ${
                  pref.emailNotify
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-transparent"
                }`}
              >
                {pref.emailNotify && (
                  <svg className="size-3.5" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </div>
            <div className="px-4 py-3.5 flex items-center justify-between">
              <span className="text-sm text-gray-700 font-medium">Push Notification</span>
              <button
                type="button"
                onClick={() => {
                  setEventPreferences((prev) =>
                    prev.map((p) =>
                      p.eventName === pref.eventName
                        ? { ...p, pushNotify: !p.pushNotify }
                        : p
                    )
                  );
                }}
                className={`size-5 rounded-md flex items-center justify-center transition-colors ${
                  pref.pushNotify
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-transparent"
                }`}
              >
                {pref.pushNotify && (
                  <svg className="size-3.5" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden lg:block mt-4 border border-gray-100 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">
                  Notification Type
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600">
                  Email Notification
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600">
                  Push Notification
                </th>
              </tr>
            </thead>
            <tbody>
              {eventPreferences.map((pref, index) => (
                <tr
                  key={`${pref.eventName}-${index}`}
                  className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors"
                >
                  <td className="py-3 px-4 text-sm text-gray-700 flex items-center gap-1">
                    {formatEventName(pref.eventName)}
                    <div className="relative group">
                      <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                      <div className="absolute z-10 left-6 top-0 w-48 p-2 bg-white border border-gray-200 shadow-md text-xs text-gray-700 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        {t(`${pref.eventName.toLowerCase()}_description`)}
                      </div>
                    </div>
                  </td>

                  <td className="py-3 px-4 text-center">
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={() => {
                          setEventPreferences((prev) =>
                            prev.map((p) =>
                              p.eventName === pref.eventName
                                ? { ...p, emailNotify: !p.emailNotify }
                                : p
                            )
                          );
                        }}
                        className={`size-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                          pref.emailNotify
                            ? "bg-blue-600 border-blue-600"
                            : "bg-white border-gray-300"
                        }`}
                      >
                        {pref.emailNotify && (
                          <svg
                            className="size-3 text-white"
                            viewBox="0 0 12 12"
                            fill="none"
                          >
                            <path
                              d="M2 6l3 3 5-5"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                  </td>

                  <td className="py-3 px-4 text-center">
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={() => {
                          setEventPreferences((prev) =>
                            prev.map((p) =>
                              p.eventName === pref.eventName
                                ? { ...p, pushNotify: !p.pushNotify }
                                : p
                            )
                          );
                        }}
                        className={`size-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                          pref.pushNotify
                            ? "bg-blue-600 border-blue-600"
                            : "bg-white border-gray-300"
                        }`}
                      >
                        {pref.pushNotify && (
                          <svg
                            className="size-3 text-white"
                            viewBox="0 0 12 12"
                            fill="none"
                          >
                            <path
                              d="M2 6l3 3 5-5"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>


    </div>
  );


  const renderEmptySection = (title: string, description: string) => (
    <div className="flex flex-col gap-4 font-['Plus_Jakarta_Sans']">
      <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      </div>
      <p className="text-gray-500 text-sm">{description}</p>
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CogIcon className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-500 text-sm font-medium">{t("under_development")}</p>
      </div>
    </div>
  );

  const renderTopBarActions = () => {
    if (activeSection === "profile") {
      if (!profileActions) return null;
      const { hasChanges, saving, save, cancel } = profileActions;
      return (
        <div className="flex items-center gap-2 shrink-0">
          <GhostButton className="h-9 px-4" disabled={!hasChanges || saving} onClick={cancel}>
            {t("page.cancel", { ns: "updateProfile", defaultValue: "Cancel" })}
          </GhostButton>
          <PrimaryButton className="h-9 px-4" disabled={!hasChanges} loading={saving} onClick={save}>
            {saving
              ? t("page.saving", { ns: "updateProfile", defaultValue: "Saving..." })
              : t("page.saveChanges", { ns: "updateProfile", defaultValue: "Save changes" })}
          </PrimaryButton>
        </div>
      );
    }

    if (activeSection === "notifications") {
      return (
        <div className="flex items-center gap-2 shrink-0">
          <GhostButton
            className="h-9 px-4"
            disabled={!hasNotificationChanges || savingNotifications}
            onClick={handleCancelNotifications}
          >
            {t("cancel", { defaultValue: "Cancel" })}
          </GhostButton>
          <PrimaryButton
            className="h-9 px-4"
            disabled={savingNotifications}
            loading={savingNotifications}
            onClick={handleSave}
          >
            {savingNotifications
              ? t("saving", { defaultValue: "Saving..." })
              : t("save_changes", { defaultValue: "Save changes" })}
          </PrimaryButton>
        </div>
      );
    }

    if (activeSection === "language") {
      return (
        <div className="flex items-center gap-2 shrink-0">
          <GhostButton
            className="h-9 px-4"
            disabled={!hasLanguageChanges || savingLanguage}
            onClick={handleCancelLanguage}
          >
            {t("cancel", { defaultValue: "Cancel" })}
          </GhostButton>
          <PrimaryButton
            className="h-9 px-4"
            disabled={savingLanguage}
            loading={savingLanguage}
            onClick={handleSaveLanguage}
          >
            {savingLanguage
              ? t("saving", { defaultValue: "Saving..." })
              : t("save_changes", { defaultValue: "Save changes" })}
          </PrimaryButton>
        </div>
      );
    }

    return null;
  };

  const renderContent = () => {
    switch (activeSection) {
      case "profile":
        return fullProfile ? (
          <UpdateProfileFeature
            member={fullProfile}
            onClose={() => {}}
            onActionsChange={setProfileActions}
            onSubmitSuccess={async () => {
              if (loggedInUserName && loggedInUserId) {
                const refreshed = await getProfileUser(loggedInUserId, loggedInUserName);
                setFullProfile(refreshed);
              }
            }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-sm text-gray-500 font-['Plus_Jakarta_Sans']">{t("loading_profile", "Loading profile...")}</p>
          </div>
        );
      case "language":
        return renderLanguageSection();
      case "notifications":
        return renderNotificationSection();
      case "privacy":
        return renderEmptySection(
          t("privacy_settings", "Privacy settings"),
          t("privacy_description", {
            defaultValue: "Control your privacy and data sharing preferences.",
          })
        );
      case "linkAccount":
        return <StripeLinkCard />;
      case "about":
        return renderEmptySection(
          t("about", "About"),
          t("about_description", {
            defaultValue: "Learn more about the application and its features.",
          })
        );
      case "terms":
        return renderEmptySection(
          t("terms_conditions", "Terms & conditions"),
          t("terms_description", {
            defaultValue: "Review our terms of service and privacy policy.",
          })
        );
      default:
        return renderLanguageSection();
    }
  };

  return (
    <div className="h-full bg-[#F4F4F5] font-['Plus_Jakarta_Sans'] overflow-hidden">
      {!isDesktop && (
      <div className="h-full flex flex-col bg-white overflow-hidden">
        {!mobileSubPageOpen ? (
          <div className="flex flex-col py-2 px-2 divide-y divide-gray-50 flex-1 overflow-y-auto">
            {sidebarItems.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => {
                  setActiveSection(item.key);
                  setMobileSubPageOpen(true);
                  setSearchParams({ tab: item.key });
                }}
                className="w-full px-3 py-3.5 flex items-center gap-3.5 text-left transition-colors hover:bg-gray-50 active:bg-gray-100"
              >
                <div className="size-6 shrink-0 flex items-center justify-center text-gray-700">
                  {item.iconSrc ? (
                    <SettingsAssetIcon src={item.iconSrc} active={false} className="size-6" />
                  ) : (
                    item.icon
                  )}
                </div>
                <span className="text-base font-medium text-gray-800 flex-1">
                  {item.label}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="flex-1 min-h-0 p-4 pb-12 bg-white overflow-y-auto">
            {["profile", "notifications", "language"].includes(activeSection) && renderTopBarActions() && (
              <div className="sticky top-0 z-10 -mx-4 -mt-4 mb-4 flex justify-end border-b border-gray-100 bg-white px-4 py-3">
                {renderTopBarActions()}
              </div>
            )}
            {renderContent()}
          </div>
        )}
      </div>
      )}

      {isDesktop && (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="h-[84px] shrink-0 px-6 bg-white border-b border-gray-100 flex items-center justify-between gap-4">
          <h1 className="text-2xl font-bold leading-8 text-gray-900">
            {t("account_settings", "Account settings")}
          </h1>
          {renderTopBarActions()}
        </div>
        <div className="flex-1 min-h-0 p-6 flex overflow-hidden">
          <div className="flex flex-row gap-6 h-full min-h-0 mx-auto justify-center overflow-hidden">
            <div className="w-72 lg:w-80 shrink-0 h-fit max-h-full bg-white rounded-3xl p-4 shadow-[0px_0px_1px_0px_rgba(0,0,0,0.06),0px_1px_2px_0px_rgba(0,0,0,0.06),0px_2px_4px_0px_rgba(0,0,0,0.04)] border border-gray-100/60 overflow-y-auto">
              <div className="flex flex-col gap-2">
                {sidebarItems.map((item) => {
                  const isActive = activeSection === item.key;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => {
                        setActiveSection(item.key);
                        setSearchParams({ tab: item.key });
                      }}
                      className={`w-full p-2 rounded-lg flex items-center gap-2 transition-colors text-left font-['Plus_Jakarta_Sans'] ${
                        isActive
                          ? "bg-blue-100 text-blue-600 font-medium"
                          : "text-neutral-600 hover:bg-gray-50 hover:text-gray-900 font-medium"
                      }`}
                    >
                      <div
                        className={`size-8 shrink-0 flex items-center justify-center ${
                          isActive ? "text-blue-600" : "text-neutral-600"
                        }`}
                      >
                        {item.iconSrc ? (
                          <SettingsAssetIcon src={item.iconSrc} active={isActive} />
                        ) : (
                          item.icon
                        )}
                      </div>
                      <span className="text-base leading-6">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div
              ref={contentScrollRef}
              className="w-[811px] max-w-full h-full max-h-full shrink-0 bg-white rounded-3xl p-4 shadow-[0px_0px_1px_0px_rgba(0,0,0,0.06),0px_1px_2px_0px_rgba(0,0,0,0.06),0px_2px_4px_0px_rgba(0,0,0,0.04)] border border-gray-100/60 overflow-y-auto"
            >
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default Settings;